import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, CircularProgress, MenuItem, Select, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
import { DeskInput } from './desk'
import { LINE } from './deskStyles'
import { prototypeService, type FigmaConnectionState } from '@/services/prototypeService'
import { ApiError } from '@/services/baseService'
import type { StimulusDraft } from './types'

/** Default and ceiling of a task's time limit (decision 2026-09-23). */
export const FIGMA_DEFAULT_LIMIT_MS = 3 * 60 * 1000
const MAX_LIMIT_MIN = 10

/**
 * The wizard's own copy for a Figma refusal, or null when it is not one.
 * Figma's rate limit on a Starter plan lasts days, so it names the moment
 * reading works again instead of "try in a moment".
 */
export function figmaErrorText(
  t: (key: string, options?: Record<string, unknown>) => string,
  err: unknown,
): string | null {
  const code = err instanceof ApiError ? (err.data?.code as string | undefined) : undefined
  if (!code?.startsWith('figma_')) return null
  const retryS = Number(err instanceof ApiError ? err.data?.retry_after_s : 0) || 0
  if (code === 'figma_rate_limited' && retryS > 0) {
    const date = new Date(Date.now() + retryS * 1000).toLocaleString(i18next.language, {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    })
    return t('wizard.errors.figma.figma_rate_limited', { date })
  }
  return t(`wizard.errors.figma.${code === 'figma_rate_limited' ? 'figma_unreachable' : code}`)
}

type Props = {
  stim: StimulusDraft
  onChange: (patch: Partial<StimulusDraft>) => void
}

/**
 * Settings of one Figma prototype task (FaceWise Pro, Z-98): the link, the
 * task wording, the screen that ends it and the time limit.
 *
 * Reading the prototype's screens needs the researcher's Figma connected
 * (OAuth, in a popup). The respondent needs something else entirely — the file
 * shared "anyone with the link" — and the connection cannot vouch for that, so
 * the two are shown as separate checks.
 */
export default function FigmaStimulusEditor({ stim, onChange }: Props) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')

  const [connection, setConnection] = useState<FigmaConnectionState | null>(null)
  const [unavailable, setUnavailable] = useState(false)
  const [inspecting, setInspecting] = useState(false)
  const [inspectError, setInspectError] = useState('')
  const inspectedUrlRef = useRef<string>(stim.figmaFrames?.length ? (stim.url ?? '') : '')

  const loadConnection = useCallback(async () => {
    try {
      setConnection(await prototypeService.connection())
    } catch (err) {
      if (err instanceof ApiError && (err.status === 503 || err.status === 403)) setUnavailable(true)
      setConnection({ connected: false, handle: '' })
    }
  }, [])

  useEffect(() => {
    void loadConnection()
  }, [loadConnection])

  // The popup posts {source: 'facewise-figma'} when it is done; whatever it
  // says, the connection is re-read from our own API rather than trusted.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.data && typeof e.data === 'object' && e.data.source === 'facewise-figma') void loadConnection()
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [loadConnection])

  const connect = async () => {
    // Opened synchronously on the click, then pointed at Figma: a popup opened
    // after an await is a popup the browser blocks.
    const popup = window.open('', 'figma-oauth', 'width=560,height=720')
    try {
      const url = await prototypeService.oauthUrl()
      if (popup) popup.location.href = url
      else window.location.href = url
    } catch {
      popup?.close()
    }
  }

  const inspect = useCallback(
    async (url: string) => {
      const trimmed = url.trim()
      if (!trimmed || trimmed === inspectedUrlRef.current) return
      inspectedUrlRef.current = trimmed
      setInspecting(true)
      setInspectError('')
      try {
        const info = await prototypeService.inspect(trimmed)
        onChange({
          figmaFrames: info.frames.map((f) => ({ node_id: f.node_id, name: f.name })),
          figmaPublic: info.is_public,
          goalNodeId: info.frames.some((f) => f.node_id === stim.goalNodeId) ? stim.goalNodeId : '',
          meta: t('wizard.stimulus.figma.screens', { count: info.frames.length }),
        })
      } catch (err) {
        inspectedUrlRef.current = ''
        setInspectError(figmaErrorText(t, err) ?? t('wizard.stimulus.figma.inspectFailed'))
        onChange({ figmaFrames: [], figmaPublic: null })
      } finally {
        setInspecting(false)
      }
    },
    [onChange, stim.goalNodeId, t],
  )

  // Re-read once the account gets connected with a link already pasted.
  useEffect(() => {
    if (connection?.connected && stim.url && !stim.figmaFrames?.length) void inspect(stim.url)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection?.connected])

  const limitMin = Math.round((stim.exposureMs ?? FIGMA_DEFAULT_LIMIT_MS) / 60000)
  const frames = stim.figmaFrames ?? []

  const note = (ok: boolean, text: string) => (
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', fontSize: 13, color: ok ? c.ink56 : c.warning }}>
      {ok ? (
        <CheckCircleOutlineIcon sx={{ fontSize: 15, color: c.success, mt: '1px' }} />
      ) : (
        <WarningAmberOutlinedIcon sx={{ fontSize: 15, mt: '1px' }} />
      )}
      <span>{text}</span>
    </Box>
  )

  if (unavailable) {
    return <Typography sx={{ fontSize: 12.5, color: c.ink56 }}>{t('wizard.stimulus.figma.unavailable')}</Typography>
  }

  return (
    <Stack spacing={1.25} sx={{ width: '100%' }}>
      {connection === null ? (
        <CircularProgress size={16} sx={{ color: c.coral }} />
      ) : connection.connected ? (
        note(true, t('wizard.stimulus.figma.connectedAs', { handle: connection.handle || 'Figma' }))
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 13, color: c.ink56, flex: 1, minWidth: 200 }}>
            {t('wizard.stimulus.figma.connectHint')}
          </Typography>
          <Button variant="outlined" size="small" onClick={() => void connect()}>
            {t('wizard.stimulus.figma.connect')}
          </Button>
        </Box>
      )}

      <DeskInput
        value={stim.url ?? ''}
        onChange={(e) => onChange({ url: e.target.value, name: e.target.value, figmaFrames: [], figmaPublic: null })}
        onBlur={(e) => connection?.connected && void inspect(e.target.value)}
        placeholder={t('wizard.stimulus.figma.urlPlaceholder')}
      />
      {inspecting && <Typography sx={{ fontSize: 12, color: c.ink56 }}>{t('wizard.stimulus.figma.inspecting')}</Typography>}
      {inspectError && note(false, inspectError)}
      {frames.length > 0 &&
        (stim.figmaPublic === false
          ? note(false, t('wizard.stimulus.figma.notPublic'))
          : note(stim.figmaPublic === true, t(stim.figmaPublic ? 'wizard.stimulus.figma.public' : 'wizard.stimulus.figma.publicUnknown')))}

      <DeskInput
        multiline
        minRows={2}
        maxRows={5}
        value={stim.payload ?? ''}
        onChange={(e) => onChange({ payload: e.target.value })}
        placeholder={t('wizard.stimulus.figma.taskPlaceholder')}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 140px' }, gap: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{t('wizard.stimulus.figma.goal')}</Typography>
          <Select
            value={stim.goalNodeId ?? ''}
            displayEmpty
            disabled={frames.length === 0}
            onChange={(e) => onChange({ goalNodeId: String(e.target.value) })}
            sx={{
              height: 42,
              fontSize: 14.5,
              borderRadius: '10px',
              bgcolor: '#fff',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: LINE },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.coral, borderWidth: 1 },
            }}
          >
            <MenuItem value="">{t('wizard.stimulus.figma.noGoal')}</MenuItem>
            {frames.map((f) => (
              <MenuItem key={f.node_id} value={f.node_id}>
                {f.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{t('wizard.stimulus.figma.limit')}</Typography>
          <DeskInput
            type="number"
            value={limitMin}
            onChange={(e) => {
              const mins = Math.max(1, Math.min(MAX_LIMIT_MIN, Math.round(Number(e.target.value) || 0)))
              onChange({ exposureMs: mins * 60000 })
            }}
            suffix={t('wizard.stimulus.figma.limitUnit')}
            slotProps={{ htmlInput: { min: 1, max: MAX_LIMIT_MIN, 'aria-label': t('wizard.stimulus.figma.limit') } }}
          />
        </Box>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: c.ink48, lineHeight: 1.45 }}>{t('wizard.stimulus.figma.shareHint')}</Typography>
    </Stack>
  )
}
